import { registerModal, useModal } from '@gluedigital/modal';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSourcesStore } from 'src/hooks/useSourcesStore';
import { FeedbackEnum, SourceURLFeedback } from 'src/types';

import './FeedbackModal.sass';
import Sources from 'src/components/Sources/Sources';
import { useIsMobile } from 'src/hooks/useIsMobile';

const FeedbackModal = ({
	handleFeedback,
	messageId,
	feedback
}: {
	handleFeedback: (
		feedback: string,
		comment?: string,
		sourceFeedback?: SourceURLFeedback[],
		internetSourceFeedback?: SourceURLFeedback[]
	) => void;
	messageId: string;
	feedback: FeedbackEnum;
}) => {
	const intl = useIntl();
	const modal = useModal();
	const isMobile = useIsMobile();
	const { sources, internetSources } = useSourcesStore();

	const [sourceFeedback, setSourceFeedback] = useState<SourceURLFeedback[]>([]);
	const [sourceInternetFeedback, setSourceInternetFeedback] = useState<SourceURLFeedback[]>([]);
	const [showMobileSources, setShowMobileSources] = useState<boolean>(false);
	const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
	const [comment, setComment] = useState<string>('');

	const handleSubmit = (e) => {
		e.preventDefault();
		if (!isMobile || showMobileSources || showConfirmation) {
			if ((sources.length === sourceFeedback.length && internetSources.length === sourceInternetFeedback.length) || showConfirmation) {
				handleFeedback(feedback, comment, sourceFeedback, sourceInternetFeedback);
				modal.hide();
			} else {
				setShowConfirmation(true);
			}
		} else {
			setShowMobileSources(true);
		}
	};

	const handleSourceUrlFeedback = (newSourceUrlFeedback: SourceURLFeedback) => {
		const newSourceFeedback = [...sourceFeedback.filter((sourceUrlFeedback) => sourceUrlFeedback.id !== newSourceUrlFeedback.id)];
		if (newSourceUrlFeedback.feedback !== undefined) {
			newSourceFeedback.push(newSourceUrlFeedback);
		}
		setSourceFeedback(newSourceFeedback);
	};

	const handleSourceUrlInternetFeedback = (newSourceUrlFeedback: SourceURLFeedback) => {
		const newSourceFeedback = [...sourceInternetFeedback.filter((sourceUrlFeedback) => sourceUrlFeedback.id !== newSourceUrlFeedback.id)];
		if (newSourceUrlFeedback.feedback !== undefined) {
			newSourceFeedback.push(newSourceUrlFeedback);
		}
		setSourceInternetFeedback(newSourceFeedback);
	};

	return (
		<div id="feedback-modal-content" className="modal-content">
			<header id="feedback-modal-header">
				<h1>
					<span>
						<FormattedMessage id="feedback-modal.title" />
					</span>
				</h1>
				<span className="icon icon-cross" onClick={() => modal.hide()} role="button" />
			</header>
			<div className="modal-content-body">
				{showConfirmation && (
					<div className="modal-content-confirm">
						<label>
							<h2 className="send-feedback-text">
								<FormattedMessage id="feedback-modal.label-confirm" />
							</h2>
						</label>
						<button onClick={handleSubmit} className="filled">
							<FormattedMessage id="send" />
						</button>
						<button onClick={() => setShowConfirmation(false)}>
							<FormattedMessage id="return" />
						</button>
					</div>
				)}
				{(!isMobile || showMobileSources) && !showConfirmation && (
					<>
						<div className="modal-content-sources">
							<Sources
								sources={sources}
								messageId={messageId}
								handleSourceURLFeedback={handleSourceUrlFeedback}
								sourceFeedback={sourceFeedback}
								internet={false}
							/>
							{internetSources.length > 0 && (
								<>
									<h3>
										{' '}
										<FormattedMessage id="internet-sources" />
									</h3>
									<Sources
										sources={internetSources}
										messageId={messageId}
										handleSourceURLFeedback={handleSourceUrlInternetFeedback}
										sourceFeedback={sourceInternetFeedback}
										internet={true}
									/>
								</>
							)}
						</div>

						{isMobile && (
							<>
								<button onClick={handleSubmit} className="button-send-sources-feedback filled">
									<FormattedMessage id="send" />
								</button>
								<button
									onClick={() => {
										setShowMobileSources(false);
									}}
									className="button-return-sources-feedback filled"
								>
									<FormattedMessage id="return" />
								</button>
							</>
						)}
					</>
				)}
				{(!isMobile || !showMobileSources) && !showConfirmation && (
					<div className="modal-content-form">
						<form>
							<h2 className="send-feedback-text-sources-reviewed">
								{sourceFeedback.length}/{sources.length} <FormattedMessage id="feedback-modal.sources-reviewed" />
							</h2>
							<h2 className="send-feedback-text-sources-reviewed">
								{sourceInternetFeedback.length}/{internetSources.length} <FormattedMessage id="feedback-modal.internet-sources-reviewed" />
							</h2>
							<label>
								<h2 className="send-feedback-text">
									<FormattedMessage id={`feedback-modal.label-${feedback === FeedbackEnum.LIKE ? 'like' : 'dislike'}`} />
								</h2>
								<textarea
									name="comment"
									onChange={(e) => setComment(e.target.value)}
									value={comment}
									cols={30}
									rows={5}
									placeholder={intl.formatMessage({
										id: 'feedback-modal.placeholder'
									})}
								/>
							</label>

							<button onClick={handleSubmit} className="filled">
								<FormattedMessage id={isMobile ? 'next' : 'send'} />
							</button>
						</form>
					</div>
				)}
			</div>
		</div>
	);
};

registerModal('feedback-modal', FeedbackModal);
